import { Directive, Input, OnChanges, computed, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Asset, ExecutionType, InstrumentType, Quote } from '@zero/shared/util-models';
import { FormCache, MinimumQuantitySurcharge, OrderFormGroup, TradeFacade, TradeService } from '@zero/trade/domain';
import { defaultQuantityFormat, defaultQuoteFormat } from 'constants/shared-constants';

@Directive()
export class TradeConfirmDataBase implements OnChanges {
  @Input() public quote?: Quote;
  @Input() public cache?: FormCache;
  @Input() public orderVolume?: number;
  @Input() public minimumQuantitySurcharge?: MinimumQuantitySurcharge;

  public asset?: Asset;
  public form?: FormGroup<OrderFormGroup>;
  public orderValue?: number;
  public execType?: ExecutionType;
  public identifier?: string;
  public provision?: number;
  public discount?: number;
  public readonly quoteFormat = defaultQuoteFormat;
  public quantityFormat = defaultQuantityFormat;

  readonly #isCrypto = computed(() => this.asset?.instrumentType === InstrumentType.Crypto);

  public readonly tradeService = inject(TradeService);
  public readonly tradeFacade = inject(TradeFacade);

  public ngOnChanges(): void {
    if (!this.cache) {
      return;
    }

    const { asset, form } = this.cache;
    this.asset = asset;
    this.form = form;
    this.#setProvision();
    this.#setOrderValue();
    this.#setExecType();
    this.#setQuantityFormat();
    this.identifier = asset.instrumentType === InstrumentType.Crypto ? asset.shortName : asset.isin;
    this.execType === 'tsl' && this.#setTickingStopPrice();
  }

  public get isBuy(): boolean {
    return this.form.get('direction').value === 'buy';
  }

  public get isSell(): boolean {
    return this.form.get('direction').value === 'sell';
  }

  public get isEstimatedOrderValue(): boolean {
    switch (this.execType) {
      case 'market':
        return true;
      case 'stop':
        return this.isSell;
      default:
        return false;
    }
  }

  public removeFractionalOrder = (): void => this.tradeFacade.removeFractionalOrder();

  readonly #setOrderValue = (): void => {
    if (this.minimumQuantitySurcharge?.value) {
      const { value } = this.minimumQuantitySurcharge;
      this.orderValue = this.isSell ? this.orderVolume + value * -1 : this.orderVolume + value;
    } else {
      this.orderValue = this.orderVolume;
    }

    this.orderValue = this.isSell ? this.orderValue + -this.provision : this.orderValue + this.provision;
  };

  readonly #setProvision = (): void => {
    if (!this.#isCrypto()) {
      this.provision = 0.0;
      return;
    }

    const { tradingFees } = this.asset;
    if (!tradingFees?.feePct) {
      return;
    }

    this.provision = this.orderVolume * (tradingFees.feePct / 100);
  };

  readonly #setTickingStopPrice = (): void => {
    const optionsForm = this.form.get('optionsForm');
    if (optionsForm.get('initialStopPrice').value || !this.quote) {
      return;
    }

    const { bid } = this.quote;
    const calculatedStopPrice =
      optionsForm.get('trailingType').value === 'abs'
        ? bid - optionsForm.get('trailingAbs').value
        : bid - bid * (optionsForm.get('trailingRel').value / 100);
    this.tradeService.tickingStopPrice.next(Number(calculatedStopPrice.toFixed(2)));
  };

  readonly #setExecType = (): void => {
    const { form } = this.cache;
    if (!form) {
      return;
    }
    const execType = form.get('execType')?.value;
    const stopLimitPrice = form.get('optionsForm')?.get('stopLimitPrice')?.value;
    if (execType === 'stop' && stopLimitPrice) {
      this.execType = 'stoplimit';
    } else {
      this.execType = execType;
    }
  };

  readonly #setQuantityFormat = (): void => {
    const qtyScale = this.asset?.qtyScale ? this.asset.qtyScale : 3;
    this.quantityFormat = `1.0-${qtyScale}`;
  };
}
